import { ArrowDown, ArrowUp, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { METRICS, NOT_SET_LABEL, formatMetric } from './flexCatalog';
import type { FlexColumn, FlexGrid, FlexGridRow, FlexSort } from './pivot';

/**
 * The pivot table (R19–R24).
 *
 * Bespoke rather than DataTable, for the reason StandardReportSections gives:
 * DataTable has no pinned Totals row and no sticky columns.
 *
 * The Totals row lives in <thead> with the header, and the whole <thead> is
 * sticky — so Totals stays directly under the header without guessing the
 * header's height. Dimension cells are sticky on the left; in side-by-side the
 * first dimension column has a fixed width so the second can sit beside it.
 */

export interface FlexPivotTableProps {
  grid: FlexGrid;
  sort: FlexSort | null;
  onSortChange: (sort: FlexSort) => void;
  onToggle: (rowKey: string) => void;
}

/** Metrics open descending (biggest first); the dimension opens A→Z. Then each click flips. */
export function nextSort(current: FlexSort | null, by: FlexSort['by']): FlexSort {
  if (current && current.by === by) return { by, dir: current.dir === 'asc' ? 'desc' : 'asc' };
  return { by, dir: by === 'dimension' ? 'asc' : 'desc' };
}

const STICKY_FIRST = 'sticky left-0 z-10';
const SIDE_FIRST_WIDTH = 'w-48 min-w-[12rem] max-w-[12rem]';
const STICKY_SECOND = 'sticky left-48 z-10';

function dimensionCellClass(index: number, sideBySide: boolean): string {
  if (index === 0) return cn(STICKY_FIRST, sideBySide ? SIDE_FIRST_WIDTH : 'min-w-[12rem]');
  return cn(STICKY_SECOND, 'min-w-[12rem]');
}

export function FlexPivotTable({ grid, sort, onSortChange, onToggle }: FlexPivotTableProps) {
  const sideBySide = grid.dimensionColumnCount === 2;
  const dimensionColumns = grid.columns.filter((c) => c.kind === 'dimension');
  const metricColumns = grid.columns.filter((c) => c.kind === 'metric');

  const sortKeyFor = (column: FlexColumn): FlexSort['by'] => (column.kind === 'dimension' ? 'dimension' : column.metric!);

  const headerCell = (column: FlexColumn, index: number) => {
    const by = sortKeyFor(column);
    const active = sort?.by === by;
    // Both dimension columns sort the rows by dimension, so only the first shows it.
    const showArrow = active && (column.kind === 'metric' || index === 0);
    const Arrow = sort?.dir === 'asc' ? ArrowUp : ArrowDown;
    const isMetric = column.kind === 'metric';
    return (
      <th
        key={column.key}
        scope="col"
        aria-sort={showArrow ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : undefined}
        className={cn(
          'border-b border-slate-300 bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-600',
          isMetric ? 'text-right' : cn('text-left', dimensionCellClass(index, sideBySide)),
        )}
      >
        <button
          type="button"
          onClick={() => onSortChange(nextSort(sort, by))}
          className={cn(
            'inline-flex items-center gap-1 whitespace-nowrap uppercase hover:text-slate-900',
            isMetric && 'flex-row-reverse',
          )}
        >
          <span>{column.label || 'Totals'}</span>
          {showArrow && <Arrow className="h-3 w-3" aria-hidden="true" />}
        </button>
      </th>
    );
  };

  const valueCells = (row: FlexGridRow, cellClass: string) =>
    row.values.map((value, i) => {
      const metric = grid.metrics[i];
      return (
        <td key={metric} className={cn('whitespace-nowrap px-3 py-2 text-right tabular-nums', cellClass)}>
          {formatMetric(value, METRICS[metric].format)}
        </td>
      );
    });

  const labelCells = (row: FlexGridRow, bg: string) =>
    row.labels.map((label, i) => {
      const isFirst = i === 0;
      const indent = row.level === 1 && isFirst;
      return (
        <td
          key={dimensionColumns[i]?.key ?? i}
          className={cn('border-b border-slate-100 px-3 py-2 text-left', bg, dimensionCellClass(i, sideBySide))}
        >
          <div className={cn('flex items-center gap-1', indent && 'pl-6', sideBySide && 'min-w-0')}>
            {isFirst && row.hasChildren && (
              <button
                type="button"
                aria-expanded={row.expanded}
                aria-label={`${row.expanded ? 'Collapse' : 'Expand'} ${label}`}
                onClick={() => onToggle(row.key)}
                className="rounded p-0.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              >
                {row.expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            )}
            <span
              title={label}
              className={cn(sideBySide ? 'truncate' : 'whitespace-nowrap', row.notSet && label === NOT_SET_LABEL && 'italic text-slate-500')}
            >
              {label}
            </span>
          </div>
        </td>
      );
    });

  return (
    <table className="min-w-full border-separate border-spacing-0 text-sm">
      <thead className="sticky top-0 z-20">
        <tr>
          {dimensionColumns.map((c, i) => headerCell(c, i))}
          {metricColumns.map((c, i) => headerCell(c, i + dimensionColumns.length))}
        </tr>
        <tr className="font-semibold text-slate-900" data-row="totals">
          {grid.totals.labels.map((label, i) => (
            <th
              key={dimensionColumns[i]?.key ?? i}
              scope="row"
              className={cn('border-b border-slate-300 bg-slate-50 px-3 py-2 text-left', dimensionCellClass(i, sideBySide))}
            >
              {label}
            </th>
          ))}
          {valueCells(grid.totals, 'border-b border-slate-300 bg-slate-50')}
        </tr>
      </thead>
      <tbody>
        {grid.rows.map((row) => (
          <tr key={row.key} className={cn(row.hasChildren && 'font-medium')}>
            {labelCells(row, 'bg-white')}
            {valueCells(row, 'border-b border-slate-100 text-slate-700')}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
